import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";

/**
 * Founder-led trust, in place of social proof we have not earned yet.
 *
 * TRUTHFULNESS CONTRACT
 * The quote below is Travis's own wording, taken from the founder story on the
 * About page - it is not a paraphrase written for marketing. The photo is the
 * real founder photo already in the project (/founder.jpg). Nothing here adds
 * credentials, professional titles, clean-time claims, treatment expertise or
 * recovery outcomes, because none of those have been provided. Social links are
 * intentionally absent: no real ones exist in the project, and inventing them
 * would be fabrication.
 */
const FOUNDER_QUOTE =
  "Sponsor Me wasn't created because I studied addiction from the outside. I built it because addiction was part of my life for more than 20 years, and I know how important real human connection can be in recovery.";

export default function FounderNote() {
  const [photoFailed, setPhotoFailed] = React.useState(false);

  return (
    <section
      data-testid="home-founder-note"
      aria-labelledby="founder-note-heading"
      className="max-w-7xl mx-auto px-4 py-14 lg:py-16"
    >
      <div className="rounded-3xl bg-white border border-[#E3DED4] overflow-hidden grid lg:grid-cols-[320px_1fr]">
        <div className="relative bg-[#F2EFE8] min-h-[260px] lg:min-h-full">
          {!photoFailed ? (
            <img
              src="/founder.jpg"
              alt="Travis, founder of Sponsor Me"
              onError={() => setPhotoFailed(true)}
              data-testid="home-founder-photo"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#7A868B] text-sm">
              Travis, Founder
            </div>
          )}
        </div>

        <div className="p-6 sm:p-9 flex flex-col justify-center">
          <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#1F6F78]">
            Built from lived experience
          </p>
          <h2
            id="founder-note-heading"
            data-testid="founder-note-heading"
            className="mt-2 font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
          >
            Why I Built Sponsor Me
          </h2>
          <Quote className="mt-5 w-7 h-7 text-[#B9DFE3]" aria-hidden="true" />
          <blockquote
            data-testid="founder-quote"
            className="mt-2 text-lg text-[#1F2A2E] leading-8 max-w-2xl"
          >
            {FOUNDER_QUOTE}
          </blockquote>
          <p data-testid="founder-attribution" className="mt-4 text-sm font-semibold text-[#4B5A60]">
            - Travis, Founder of Sponsor Me
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              onClick={() => trackOutcome(HEYCATCH_EVENTS.FOUNDER_STORY_CLICKED)}
              className="bg-[#1F6F78] hover:bg-[#185B62] text-white h-11 px-6"
            >
              <Link to="/about" data-testid="founder-read-story-cta">
                Read My Story <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              onClick={() => trackOutcome(HEYCATCH_EVENTS.PRODUCT_UPDATES_CLICKED)}
              className="h-11 px-6 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
            >
              <Link to="/updates" data-testid="founder-updates-cta">
                Building Sponsor Me in public
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
